/**
 * openseadragon-curtainsync.js
 * 
 * 
 * Adapted from Morgan Schwartz's https://github.com/locomo/openseadragon-curtain-sync
 * 
 **/ 
import OpenSeadragon, { EventSource } from 'openseadragon'

class CurtainMode extends EventSource {

  constructor(args) {
    super(args)

    this.images = args.images;
    this.startingZoom = args.zoom;
    this.startingPan = args.pan;
    this.isMobile = (typeof window.orientation !== 'undefined') || (navigator.userAgent.indexOf('IEMobile') !== -1);
    // this.isMobile = true; // For testing

    if (this.isMobile) {
      this.clipFactorX = 0.5;
      this.clipFactorY = 0.5;
    } else {
      this.clipFactorX = 0;
      this.clipFactorY = 0;
    }

    var ops = args.osdOptions;
    ops.element = args.container;
    this.viewer = OpenSeadragon(ops);

    this.viewer.canvas.style.outline = 'none'; // so we don't see the browser's selection rectangle when we click

    this.tracker = new OpenSeadragon.MouseTracker({
      element: this.viewer.canvas,
      moveHandler: (event) => {
        if (this.isMobile) {
          return;
        }

        this.dragClip(event.position, true, true);
      },
      pressHandler: (event) => {
        if (!this.isMobile) {
          return;
        }

        var shownImages = this.getShownImages();
        if (shownImages.length <= 1) {
          return;
        }

        var viewerPos = new OpenSeadragon.Point(this.viewer.container.clientWidth * this.clipFactorX,
          this.viewer.container.clientHeight * this.clipFactorY);

        var threshold = 20;

        if (shownImages.length === 2) {
          if (Math.abs(viewerPos.x - event.position.x) < threshold) {
            this.isDraggingClipX = true;
          }
        } else if (shownImages.length === 3) {
          if (Math.abs(viewerPos.x - event.position.x) < threshold && event.position.y < viewerPos.y + threshold) {
            this.isDraggingClipX = true;
          }

          if (Math.abs(viewerPos.y - event.position.y) < threshold) {
            this.isDraggingClipY = true;
          }
        }
      },
      releaseHandler: (event) => {
        this.isDraggingClipX = false;
        this.isDraggingClipY = false;
      }
    });

    this.viewer.addHandler('viewport-change', () => {
      this.updateClip();
      this.raiseEvent('change:viewport');
    });

    this.viewer.addHandler('add-item-failed', (event) => {
      this.raiseEvent('add-item-failed', event);
    });

    this.viewer.addHandler('canvas-drag', (event) => {
      if (!this.isDraggingClipX && !this.isDraggingClipY) {
        return;
      }

      event.preventDefaultAction = true;
      this.dragClip(event.position, this.isDraggingClipX, this.isDraggingClipY);
    });

    this.images.forEach((image, i) => {
      image.curtain = {};

      this.viewer.addTiledImage({
        tileSource: image.tileSource,
        opacity: image.shown,
        success: (event) => {
          image.curtain.tiledImage = event.item;
          image.curtain.tiledImage.setOpacity(image.shown ? 1 : 0);

          if (i === 0) {
            if (this.startingZoom) {
              this.viewer.viewport.zoomTo(this.startingZoom, null, true);
            }

            if (this.startingPan) {
              this.viewer.viewport.panTo(this.startingPan, true);
            }
          }

          this.updateClip();
        }
      });
    });   
  }

  destroy() {
    this.images.forEach((image) => {
      delete image.curtain;
    });

    this.tracker.destroy();
    this.viewer.destroy();
  }

  getZoom() {
    return this.viewer.viewport.getZoom();
  }

  
  setZoom(zoom) {
    this.viewer.viewport.zoomTo(zoom, null, true);
    this.startingZoom = zoom;
  }

  
  getPan() {
    return this.viewer.viewport.getCenter();
  }

  setPan(pan) {
    this.viewer.viewport.panTo(pan, true);
    this.startingPan = pan;
  }

  zoomIn() {
    this.viewer.viewport.zoomBy(this.viewer.zoomPerClick);
    this.viewer.viewport.applyConstraints();
  }

  zoomOut() {
    this.viewer.viewport.zoomBy(1 / this.viewer.zoomPerClick);
    this.viewer.viewport.applyConstraints();
  }

  updateImageShown(image) {
    if (image.curtain.tiledImage) {
      image.curtain.tiledImage.setOpacity(image.shown ? 1 : 0);
    }
  }

  updateClip() {
    var viewerPos = new OpenSeadragon.Point(this.viewer.container.clientWidth * this.clipFactorX,
      this.viewer.container.clientHeight * this.clipFactorY);

    var viewportPos = this.viewer.viewport.pointFromPixel(viewerPos, true);
    var tiledImage, imageSize, imagePos, clip;
    var shownImages = this.getShownImages();

    if (shownImages.length > 1) {
      tiledImage = shownImages[1].curtain.tiledImage;
      if (tiledImage) {
        imageSize = tiledImage.getContentSize();
        imagePos = tiledImage.viewportToImageCoordinates(viewportPos);
        var x = Math.min(imageSize.x, Math.max(0, imagePos.x));
        clip = new OpenSeadragon.Rect(x, 0, imageSize.x, imageSize.y);
        tiledImage.setClip(clip);
      }
    }

    if (shownImages.length > 2) {
      tiledImage = shownImages[2].curtain.tiledImage;
      if (tiledImage) {
        imageSize = tiledImage.getContentSize();
        imagePos = tiledImage.viewportToImageCoordinates(viewportPos);
        var y = Math.min(imageSize.y, Math.max(0, imagePos.y));
        clip = new OpenSeadragon.Rect(0, y, imageSize.x, imageSize.y);
        tiledImage.setClip(clip);
      }
    }
  }

  
  dragClip(position, dragX, dragY) {
    if (dragX) {
      this.clipFactorX = position.x / this.viewer.container.clientWidth;
    }

    if (dragY) {
      this.clipFactorY = position.y / this.viewer.container.clientHeight;
    }

    this.updateClip();
  }

  
  getShownImages() {
    return this.images.filter((image) => {
      return image.shown;
    });
  }
}


var SyncMode = function (args) {
  var self = this;

  this.images = args.images;
  this.startingZoom = args.zoom;
  this.startingPan = args.pan;
  this.leadingImage = null;

  OpenSeadragon.EventSource.call(this);

  this.innerContainer = document.createElement('div');
  this.innerContainer.style.display = 'flex';
  this.innerContainer.style.height = '100%';
  args.container.appendChild(this.innerContainer);

  this.images.forEach(function (image, i) {
    image.sync = {};

    image.sync.container = document.createElement('div');
    image.sync.container.style.flexGrow = 1;
    self.innerContainer.appendChild(image.sync.container);

    if (!image.shown) {
      image.sync.container.style.display = 'none';
    }

    var ops = args.osdOptions;
    ops.element = image.sync.container;
    ops.tileSources = image.tileSource;
    image.sync.viewer = OpenSeadragon( ops );

    image.sync.viewer.canvas.style.outline = 'none'; // so we don't see the browser's selection rectangle when we click

    image.sync.viewer.addHandler('open', function () {
      if (self.startingZoom) {
        image.sync.viewer.viewport.zoomTo(self.startingZoom, null, true);
      }

      if (self.startingPan) {
        image.sync.viewer.viewport.panTo(self.startingPan, true);
      } else {
        var bounds = image.sync.viewer.world.getHomeBounds();
        var pan = new OpenSeadragon.Point(bounds.x + (bounds.width / 2), bounds.y + (bounds.height / 2));
        image.sync.viewer.viewport.panTo(pan, true);
      }
    });

    image.sync.viewer.addHandler('add-item-failed', function (event) {
      self.raiseEvent('add-item-failed', event);
    });

    var changeHandler = function () {
      if (self.leadingImage && self.leadingImage !== image) {
        return;
      }

      self.leadingImage = image;
      self.images.forEach(function (image2) {
        if (image2 === image) {
          return;
        }

        image2.sync.viewer.viewport.zoomTo(image.sync.viewer.viewport.getZoom());
        image2.sync.viewer.viewport.panTo(image.sync.viewer.viewport.getCenter());
      });

      self.leadingImage = null;
    };

    image.sync.viewer.addHandler('zoom', function () {
      changeHandler();
    });

    image.sync.viewer.addHandler('pan', function () {
      changeHandler();
    });

    if (i === 0) {
      image.sync.viewer.addHandler('viewport-change', function () {
        self.raiseEvent('change:viewport');
      });
    }
  });
};


SyncMode.prototype = OpenSeadragon.extend({
  
  destroy: function () {
    this.images.forEach(function (image) {
      image.sync.container.parentNode.removeChild(image.sync.container);
      image.sync.viewer.destroy();
      delete image.sync;
    });

    this.innerContainer.parentNode.removeChild(this.innerContainer);
  },

  
  getZoom: function () {
    var viewer = this.images[0].sync.viewer;
    return viewer.viewport.getZoom();
  },

  
  setZoom: function (zoom) {
    var viewer = this.images[0].sync.viewer;
    viewer.viewport.zoomTo(zoom, null, true);
    this.startingZoom = zoom;
  },

  
  getPan: function () {
    var viewer = this.images[0].sync.viewer;
    return viewer.viewport.getCenter();
  },

  
  setPan: function (pan) {
    var viewer = this.images[0].sync.viewer;
    viewer.viewport.panTo(pan, true);
    this.startingPan = pan;
  },

  
  zoomIn: function () {
    var viewer = this.images[0].sync.viewer;
    viewer.viewport.zoomBy(viewer.zoomPerClick);
    viewer.viewport.applyConstraints();
  },

  
  zoomOut: function () {
    var viewer = this.images[0].sync.viewer;
    viewer.viewport.zoomBy(1 / viewer.zoomPerClick);
    viewer.viewport.applyConstraints();
  },

  
  updateImageShown: function (image) {
    image.sync.container.style.display = image.shown ? 'block' : 'none';
  }
}, OpenSeadragon.EventSource.prototype);

class CurtainSyncViewer extends EventSource {
  
  constructor(args) {
    super(args)

    console.assert(args, '[CurtainSyncViewer] args is required');
    console.assert(args.container, '[CurtainSyncViewer] args.container is required');
    console.assert(args.images, '[CurtainSyncViewer] args.images is required');
    console.assert(args.images.length > 0, '[CurtainSyncViewer] args.images must have at least 1 image');

    OpenSeadragon.EventSource.call(this);

    this.container = args.container;
    this.viewportEventThrottle = args.viewportEventThrottle || 250;
    this.lastViewportEventTime = 0;
    this.images = [];
    this.osdOptions = args.osdOptions || {};
    this.osdOptions.showNavigationControl = false; // hardcode to override this option

    if (getComputedStyle(this.container).position === 'static') {
      this.container.style.position = 'relative';
    }

    args.images.forEach((argsImage, i) => {
      console.assert(argsImage.key, '[CurtainSyncViewer] args.images[' + i + '].key is required');
      console.assert(argsImage.tileSource, '[CurtainSyncViewer] args.images[' + i + '].tileSource is required');

      var image = {
        key: argsImage.key,
        tileSource: argsImage.tileSource,
        shown: !!argsImage.shown
      };

      this.images.push(image);
    });

    this.setMode(args.mode || 'curtain');
  }

  
  getMode() {
    return this.modeKey;
  }

  
  setMode(key) {
    var self = this;

    console.assert(key === 'curtain' || key === 'sync', '[CurtainSyncViewer.setMode] Must have valid key.');
    if (key === this.modeKey) {
      return;
    }

    if (this.mode) {
      this.mode.destroy();
    }

    this.modeKey = key;

    var config = {
      container: this.container,
      images: this.images,
      zoom: this.zoom,
      pan: this.pan,
      osdOptions: OpenSeadragon.extend({}, this.osdOptions)
    };

    if (key === 'curtain') {
      this.mode = new CurtainMode(config);
    } else { // sync
      this.mode = new SyncMode(config);
    }

    this.mode.addHandler('change:viewport', () => {
      this.handleViewportChange();
    });

    this.mode.addHandler('add-item-failed', (event) => {
      this.raiseEvent('open-failed', {
        message: event.message || '',
        tileSource: event.options ? event.options.tileSource : undefined
      });
    });

    this.raiseEvent('change:mode');
  }

  
  getZoom() {
    return this.mode.getZoom();
  }

  
  setZoom(zoom) {
    console.assert(typeof zoom === 'number' && zoom > 0 && zoom < Infinity, '[CurtainSyncViewer.setZoom] zoom must be a positive number');
    this.mode.setZoom(zoom);
    this.handleViewportChange();
  }

  
  zoomIn() {
    this.mode.zoomIn();
    this.handleViewportChange();
  }

  
  zoomOut() {
    this.mode.zoomOut();
    this.handleViewportChange();
  }

  
  getPan() {
    return this.mode.getPan();
  }

  
  setPan(pan) {
    console.assert(typeof pan === 'object' && typeof pan.x === 'number' && typeof pan.y === 'number',
      '[CurtainSyncViewer.setPan] pan must be an object with an x and a y');

    this.mode.setPan(new OpenSeadragon.Point(pan.x, pan.y));
    this.handleViewportChange();
  }

  
  getImageShown(key) {
    var shown = false;
    this.images.forEach((image) => {
      if (image.key === key && image.shown) {
        shown = true;
      }
    });

    return shown;
  }

  
  setImageShown(key, shown) {
    var self = this;
    shown = !!shown;
    var changed = false;
    this.images.forEach((image) => {
      if (image.key === key && image.shown !== shown) {
        changed = true;
        image.shown = shown;
        this.mode.updateImageShown(image);
      }
    });

    if (changed) {
      this.raiseEvent('change:imageShown', {
        key: key
      });
    }
  }

  handleViewportChange() {
    var self = this;
    var zoom = this.getZoom();
    var pan = this.getPan();

    if (this.zoom !== zoom || !this.pan || this.pan.x !== pan.x || this.pan.y !== pan.y) {
      if (!this.viewportEventTimeout) {
        var now = Date.now();
        var diff = now - this.lastViewportEventTime;
        if (diff < this.viewportEventThrottle) {
          this.viewportEventTimeout = setTimeout(() => {
            this.viewportEventTimeout = null;
            this.raiseEvent('change:viewport');
            this.lastViewportEventTime = Date.now();
          }, this.viewportEventThrottle - diff);
        } else {
          this.raiseEvent('change:viewport');
          this.lastViewportEventTime = now;
        }
      }
    }

    this.zoom = zoom;
    this.pan = pan;
  }
}

export default CurtainSyncViewer